import { createContext, PropsWithChildren, SetStateAction, useContext, useEffect, useState } from 'react';
import { FormikErrors } from 'formik';

import { useCampaignError } from 'features/add-campaign/form/error/useCampaignError';
import { useSharePopup } from 'features/share/useSharePopup';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { campaignService } from 'services/campaign/campaignService';
import { CampaignDto, EditCampaignDto, NewCampaignDto } from 'services/campaign/campaignService.dto';
import {
  CampaignFormDraft,
  clearCampaignFormDraft,
  saveCampaignFormDraft,
  toDraftPhotos,
} from 'utils/formDraftStorage';

import { CampaignFormData, useCampaignForm } from './useCampaignForm';

export const MAX_PHOTOS_COUNT = 12;
export const MAX_PHOTO_SIZE = 15 * 1024 * 1024;
export const MAX_CAMPAIGN_GOAL = 1e11;
export const DESCRIPTION_LENGTH_LIMIT = 20000;
export const NAME_LENGTH_LIMIT = 255;

interface AddCampaignContextType {
  save: () => Promise<void>;
  isSaving: boolean;
  addPhoto: (file: File) => void;
  deletePhoto: (id: string) => void;
  movePhoto: (from: number, to: number) => void;
  maxPhotosCount?: number;
  formData: CampaignFormData;
  setFieldValue: (field: string, value: any) => void;
  setValues: (
    values: SetStateAction<CampaignFormData>,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<CampaignFormData>>;
  errors: FormikErrors<CampaignFormData>;
  isEditMode: boolean;
  isFormValid: boolean;
}

const AddCampaignContext = createContext<AddCampaignContextType>(null!);

export interface AddCampaignContextProps extends PropsWithChildren {
  campaign?: CampaignDto;
  onCampaignSaved?: () => void;
  draft?: CampaignFormDraft;
}

const AddCampaignProvider = ({ campaign, onCampaignSaved, draft, children }: AddCampaignContextProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { currentUser } = useCurrentUser();
  const { showAddCampaignErrorPopup } = useCampaignError();
  const { showCampaignCreatedSharePopup } = useSharePopup();

  const removeUnsafePhotos = (unsafeImageUrls: string[]) => {
    campaignForm.values.photos
      .filter(photo => photo.url && unsafeImageUrls.includes(photo.url))
      .forEach(photo => campaignForm.deletePhoto(photo.id));
  };

  const postCampaign = async (request: NewCampaignDto) => {
    setIsSaving(true);
    try {
      const createdCampaign = await campaignService.postCampaign(request);
      if (currentUser?.id) clearCampaignFormDraft(currentUser.id);
      showCampaignCreatedSharePopup(createdCampaign.data);
      onCampaignSaved && onCampaignSaved();
    } catch (error) {
      showAddCampaignErrorPopup(error, removeUnsafePhotos);
    } finally {
      setIsSaving(false);
    }
  };

  const updateCampaign = async (request: EditCampaignDto) => {
    setIsSaving(true);
    try {
      await campaignService.editCampaign(request);
      onCampaignSaved && onCampaignSaved();
    } catch (error) {
      showAddCampaignErrorPopup(error, removeUnsafePhotos);
    } finally {
      setIsSaving(false);
    }
  };

  const campaignForm = useCampaignForm(postCampaign, updateCampaign, campaign, draft);

  useEffect(() => {
    if (campaign || !currentUser?.id) return;
    const values = campaignForm.values;
    const draftToSave: CampaignFormDraft = {
      photos: toDraftPhotos(values.photos),
      videoUrl: values.videoUrl,
      moneyGoal: values.moneyGoal,
      name: values.name,
      campaignLocation: values.campaignLocation,
      description: values.description,
      categories: values.categories,
      timelineFrom: values.timelineFrom?.toISOString(),
      timelineTo: values.timelineTo?.toISOString(),
    };
    saveCampaignFormDraft(currentUser.id, draftToSave);
  }, [campaign, currentUser?.id, campaignForm.values]);

  const isEditMode = !!campaign;

  return (
    <AddCampaignContext.Provider
      value={{
        save: campaignForm.submitForm,
        isSaving,
        formData: campaignForm.values,
        maxPhotosCount: MAX_PHOTOS_COUNT,
        isFormValid: campaignForm.isValid && (campaignForm.dirty || !!draft) && !campaignForm.isValidating,
        isEditMode,
        ...campaignForm,
      }}>
      {children}
    </AddCampaignContext.Provider>
  );
};

const useAddCampaign = () => useContext(AddCampaignContext);

export { AddCampaignProvider, useAddCampaign };
